/**
 * Integrytas Group — Common Security Module (CSM)
 * fetchAllRows — page past PostgREST's max_rows cap to load a COMPLETE dataset.
 *
 * For interactive lists that only need a visible window, use `useAdaptiveList`.
 * This helper is for the cases that genuinely need every row: a TREE that must
 * build its full hierarchy, a REPORT viewed/exported in full, or a screen whose
 * client-side logic (maps, AI query, cross-tabs) assumes the entire set. Those
 * would otherwise silently stop at the cap (default 1000) — see the apiDb
 * truncation guard.
 *
 * It range-pages in `pageSize`-row windows (each <= the cap, so no window is
 * itself truncated) until a short page signals the end. The `build` callback MUST
 * apply a STABLE order — a unique key, or a non-unique sort plus a unique
 * tiebreaker — so pages don't overlap or skip rows.
 *
 * Note: this loads the whole set into memory; for tables that can grow without
 * bound (millions of rows) prefer a server-side aggregate/export instead.
 */
import { apiDb } from './api_client';

type PagedResult = { data: unknown; error: { message: string } | null };

export async function fetchAllRows<T = Record<string, unknown>>(
  build: (from: number, to: number) => PromiseLike<PagedResult>,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await build(from, from + pageSize - 1);
    if (error) throw new Error(error.message);
    const chunk = (data ?? []) as T[];
    all.push(...chunk);
    if (chunk.length < pageSize) break;
  }
  return all;
}

/** Convenience wrapper: fetch every row of a plain `table` with a column list and
 *  a stable order column (unique). Use the `build` form for embeds/filters. */
export async function fetchAllFrom<T = Record<string, unknown>>(
  table: string,
  columns: string,
  orderColumn: string,
  opts?: { ascending?: boolean; pageSize?: number },
): Promise<T[]> {
  return fetchAllRows<T>(
    (from, to) =>
      apiDb.from(table).select(columns)
        .order(orderColumn, { ascending: opts?.ascending !== false })
        .range(from, to),
    opts?.pageSize,
  );
}
